import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { Accordion, AccordionItem, AccordionPanel } from 'remote-com-shared/components/Accordion';
import { Button } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { AnimatedSection } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import {
  OverviewCTALinkWrapper,
  OverviewCTALinkText,
  OverviewLabel,
  OverviewSectionHeader,
  OverviewTitleComponent,
  OverviewDescriptionComponent,
} from '@/src/components/OverviewSection';
import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { FAQAccordionHeader, FAQSectionWrapper } from '@/src/views/ContractorManagement/styles';

const HeaderWrapper = styled(FAQAccordionHeader)`
  > button > span {
    display: flex;
    align-items: center;
    color: var(--colors-irisBlue);
  }
`;

const SecondTitle = styled.div`
  ${({ theme }) => theme.typography.titleXL}
  font-weight: 600;
  padding-bottom: 45px;
  color: var(--colors-darkBlue);
  text-align: left;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

const ImageWrapper = styled.div`
  margin-right: 9px;
  min-width: 24px;

  display: flex;
  align-content: center;
`;

const IconWrapper = styled.div`
  position: absolute;
  background-color: var(--colors-linkWater);
  top: -20px;
  left: 42%;
  padding: 10px;
  border-radius: 25px;
`;

const AccordionPanelWrapper = styled(AccordionPanel)`
  ${tabletBreakpoint(css`
    display: flex;
    flex-direction: column;
    margin: auto;
  `)}
`;

const SectionWrapper = styled(FAQSectionWrapper)`
  && {
    background-image: none;
    background-color: var(--colors-blank);
    padding-top: 0;
  }
`;

const CTATitle = styled.div`
  ${({ theme }) => theme.typography.bodyLeadBold}
  color: var(--colors-darkBlue);
  max-width: 166px;
  margin: 0 auto;
  text-align: center;
  padding-bottom: 9px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

const CTADescription = styled.div`
  ${({ theme }) => theme.typography.body}
  color: var(--colors-bayoux);
  max-width: 269px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

const QuestionWrapper = styled.div``;

const CTAButton = styled(Button)`
  && {
    padding: 6px 39px;
    margin-top: 27px;
  }
`;

const BottomCTAWrapper = styled.div`
  display: flex;
  margin-top: 50px;
`;

const BottomCTADescription = styled(CTADescription)`
  ${({ theme }) => theme.typography.bodyLeadBold}
  color: var(--colors-bayoux);
  margin-bottom: 6px;
`;

const BottomCTATitle = styled(OverviewCTALinkText)`
  ${({ theme }) => theme.typography.titleXS}
  color: var(--colors-irisBlue);

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 31px;
`;

const CTARight = styled.div`
  background-color: var(--colors-linkWater);
  border-radius: 10px;
  padding: 42px 31px 45px;
  box-sizing: border-box;
  text-align: center;
  position: relative;
  float: right;
  margin: 0 0 25px 81px;

  ${tabletBreakpoint(css`
    margin: 50px auto 30px;
  `)}
`;

const DescriptionWrapper = styled.div`
  color: var(--colors-lynch);
  opacity: 1;

  > ul,
  li {
    padding-left: 0;
    list-style: none;
    line-height: 2;

    li::before {
      content: '•';
      color: var(--colors-turquoise);
      margin-right: 10px;
    }
  }
`;

export const FAQSection = ({
  title,
  label,
  description,
  frequentlyAskedQuestions,
  htmlSerializer,
  secondTitle,
  children,
  defaultIndex,
  ...props
}) => (
  <SectionWrapper {...props}>
    <ContentContainer>
      <AnimatedSection ComponentName={OverviewSectionHeader}>
        {label && <OverviewLabel>{label} </OverviewLabel>}
        {title && <RichText render={title} Component={OverviewTitleComponent} />}
        {description && <RichText render={description} Component={OverviewDescriptionComponent} />}
      </AnimatedSection>

      {secondTitle && <RichText render={secondTitle} Component={SecondTitle} />}

      <Accordion
        collapsible
        multiple
        defaultIndex={defaultIndex ? 0 : Array.from(Array(frequentlyAskedQuestions.length).keys())}
      >
        {frequentlyAskedQuestions.map(
          (
            {
              question,
              answer,
              icon,
              bottom_image: bottomImage,
              right_cta: rightCta,
              right_title: rightTitle,
              right_description: rightDescription,
              right_button_label: rightButtonLabel,
              right_button_link: rightButtonLink,
              right_cta_icon: rightCtaIcon,
              bottom_title: bottomTitle,
              bottom_description: bottomDescription,
              bottom_link: bottomLink,
              image_bottom: imageBottom,
            },
            index
          ) => (
            <AccordionItem key={index}>
              <HeaderWrapper>
                <ImageWrapper>
                  <Image
                    src={icon.url}
                    alt={icon.alt || ''}
                    width={icon.dimensions.width}
                    height={icon.dimensions.height}
                    quality="100"
                  />
                </ImageWrapper>
                <QuestionWrapper>{RichText.asText(question)}</QuestionWrapper>
              </HeaderWrapper>
              {rightCta && (
                <AccordionPanelWrapper>
                  <CTARight>
                    <IconWrapper>
                      <Image
                        src={rightCtaIcon.url}
                        width={rightCtaIcon?.dimensions.width}
                        height={rightCtaIcon?.dimensions.height}
                        alt={rightCtaIcon?.alt || ''}
                      />
                    </IconWrapper>
                    <RichText render={rightTitle} Component={CTATitle} />
                    <RichText render={rightDescription} Component={CTADescription} />
                    <CTAButton tone="primary" href={rightButtonLink?.url} rel="nofollow">
                      {rightButtonLabel}
                    </CTAButton>
                  </CTARight>
                  <RichText
                    render={answer}
                    Component={DescriptionWrapper}
                    htmlSerializer={htmlSerializer}
                  />
                </AccordionPanelWrapper>
              )}
              {bottomImage && (
                <AccordionPanel>
                  <RichText
                    render={answer}
                    Component={DescriptionWrapper}
                    htmlSerializer={htmlSerializer}
                  />
                  <BottomCTAWrapper>
                    <Image
                      src={imageBottom?.url}
                      width={imageBottom?.dimensions.width}
                      height={imageBottom?.dimensions.height}
                      alt={imageBottom?.alt || ''}
                    />
                    <ContentWrapper>
                      <RichText render={bottomDescription} Component={BottomCTADescription} />
                      <OverviewCTALinkWrapper>
                        <RichText
                          render={bottomTitle}
                          Component={BottomCTATitle}
                          href={bottomLink?.url}
                          rel="noreferrer noopener"
                          target="_blank"
                        />
                      </OverviewCTALinkWrapper>
                    </ContentWrapper>
                  </BottomCTAWrapper>
                </AccordionPanel>
              )}
              {!rightCta && !bottomImage && (
                <AccordionPanel>
                  <RichText
                    render={answer}
                    Component={DescriptionWrapper}
                    htmlSerializer={htmlSerializer}
                  />
                </AccordionPanel>
              )}
            </AccordionItem>
          )
        )}
      </Accordion>
      {children}
    </ContentContainer>
  </SectionWrapper>
);
